import { useSyncExternalStore } from 'react';
import { errorText } from '../../../lib/apiErrors';
import { wikiErrorCode } from '../../../hooks/useWikiV2';
import { wikiUi } from '../shared/wikiUi';
import { ASK_ERRORS, ASK_TOASTS, SCOPE_REFUSALS } from './askCopy';

/**
 * askStore — the Ask the Wiki conversation, kept at module level so it
 * survives navigating to Home / a category / a page and back (prototype: the
 * thread lives in the top-level component state and only "New conversation"
 * clears it, D-ask §1.0). It is bound to the signed-in user (`bindOwner`): a
 * different user or organization in the same tab starts from empty, so one
 * person's questions never render for the next.
 *
 * State:
 *   ownerKey    "<org_id>:<user_id>" the thread belongs to
 *   epoch       bumped by reset / New conversation; late responses from an
 *               older epoch are dropped
 *   threadId    sent back as `thread_id` on the next ask (follow-ups)
 *   rows        [{id, role:'user', text}
 *                | {id, role:'ai', pending:true, question}
 *                | {id, role:'ai', turn}                       (contract Turn)
 *                | {id, role:'ai', error, code, question}]     (request failed)
 *   scope       {categoryId, pageId} — the selected scope chip (category/page
 *               scopes need the `wiki_category_assistants` plan feature AND the
 *               category's — and its parent's — `assistant_enabled`)
 *   extraScope  {categoryId, pageId} | null — a preset scope that is not a
 *               top-level chip (a subcategory or a page), kept as its own chip
 *   input       composer draft
 *   sending     an ask is in flight (the composer can't send another)
 *   hydrating   restoring the thread after a reload
 *   fbOpen      turn_id whose 👎 panel is open (one at a time) · fbReason · fbText
 *   trace       {turn_id: bool} trace panel open
 *   exact       {"<turn_id>-<n>": bool} "exact wording" open
 *   busy        {turn_id: 'feedback'|'handoff'} request in flight for a turn
 */

export const SCOPE_ALL = Object.freeze({ categoryId: null, pageId: null });

const SESSION_KEY = 'nurturecrm.wiki.ask.thread';

let epochSeq = 0;
let rowSeq = 0;

function initialState(ownerKey = null) {
    epochSeq += 1;
    return {
        ownerKey,
        epoch: epochSeq,
        threadId: null,
        rows: [],
        scope: SCOPE_ALL,
        extraScope: null,
        input: '',
        sending: false,
        hydrating: false,
        fbOpen: null,
        fbReason: null,
        fbText: '',
        trace: {},
        exact: {},
        busy: {},
    };
}

let state = initialState();
const listeners = new Set();

function emit() {
    listeners.forEach((listener) => listener());
}

function setState(patch) {
    state = { ...state, ...patch };
    emit();
}

const nextRowId = () => {
    rowSeq += 1;
    return `ask-row-${rowSeq}`;
};

const enc = (id) => encodeURIComponent(String(id));

// ── Session persistence (thread id only; turns are refetched) ───────────────

function readSession() {
    try {
        const raw = window.sessionStorage.getItem(SESSION_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
}

function writeSession() {
    try {
        if (state.threadId && state.ownerKey) {
            window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ owner: state.ownerKey, threadId: state.threadId }));
        } else {
            window.sessionStorage.removeItem(SESSION_KEY);
        }
    } catch {
        // Storage blocked (private mode, quota): the in-memory thread still works.
    }
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * User-facing text for a failed Ask request. Plan limits and other structured
 * errors carry their own `message`; the Ask routes' bare codes map to copy.
 */
export function askErrorText(error) {
    const detail = error?.response?.data?.detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)
        && typeof detail.message === 'string' && detail.message.trim()) {
        return detail.message;
    }
    const code = wikiErrorCode(error);
    if (code && ASK_ERRORS[code]) return ASK_ERRORS[code];
    const fallback = "Ask the Wiki didn't answer. Please try again.";
    if (typeof detail === 'string' && /^[a-z0-9_.:-]+$/.test(detail)) return fallback;
    return errorText(error, fallback);
}

/** The turn as the UI reads it: arrays always arrays. */
export function normalizeTurn(turn) {
    const t = turn && typeof turn === 'object' ? turn : {};
    return {
        ...t,
        lines: Array.isArray(t.lines) ? t.lines : [],
        sources: Array.isArray(t.sources) ? t.sources : [],
        stance: Array.isArray(t.stance) ? t.stance : [],
    };
}

/** `kind` is the render switch; derive it from `status` for an older payload. */
export function turnKind(turn) {
    if (turn?.kind === 'answer' || turn?.kind === 'gap' || turn?.kind === 'notice') return turn.kind;
    if (turn?.status === 'answered') return 'answer';
    if (turn?.status === 'gap' || turn?.status === 'unconfirmed') return 'gap';
    return 'notice';
}

function downToast(response) {
    const proposal = response?.proposal;
    if (proposal && proposal.created) return ASK_TOASTS.downProposal;
    if (proposal) return ASK_TOASTS.downJoined;
    return ASK_TOASTS.downLesson;
}

function findTurn(turnId) {
    const row = state.rows.find((r) => r.turn && r.turn.turn_id === turnId);
    return row ? row.turn : null;
}

function patchTurn(turnId, patch) {
    setState({
        rows: state.rows.map((row) => (row.turn && row.turn.turn_id === turnId
            ? { ...row, turn: { ...row.turn, ...patch } }
            : row)),
    });
}

function replaceRow(rowId, next) {
    setState({ rows: state.rows.map((row) => (row.id === rowId ? next : row)) });
}

function setBusy(turnId, value) {
    const busy = { ...state.busy };
    if (value) busy[turnId] = value;
    else delete busy[turnId];
    setState({ busy });
}

function requestBody(question) {
    const body = { question };
    if (state.scope.pageId) body.page_id = state.scope.pageId;
    else if (state.scope.categoryId) body.category_id = state.scope.categoryId;
    if (state.threadId) body.thread_id = state.threadId;
    return body;
}

async function runAsk(client, pendingId, question) {
    const { epoch } = state;
    try {
        const turn = await client.post('/wiki/ask', requestBody(question));
        if (state.epoch !== epoch) return null;
        replaceRow(pendingId, { id: pendingId, role: 'ai', turn: normalizeTurn(turn) });
        setState({ threadId: turn?.thread_id || state.threadId, sending: false });
        writeSession();
        return turn;
    } catch (error) {
        if (state.epoch !== epoch) return null;
        const code = wikiErrorCode(error);
        replaceRow(pendingId, { id: pendingId, role: 'ai', error: askErrorText(error), code, question });
        // The thread is gone (expired / not ours): the next ask starts a new one.
        // A refused SCOPE (category assistant off / not in the plan): back to
        // "All categories", so "Try again" asks the whole Wiki.
        setState({
            sending: false,
            ...(code === 'wiki_thread_not_found' ? { threadId: null } : {}),
            ...(SCOPE_REFUSALS.includes(code) ? { scope: SCOPE_ALL, extraScope: null } : {}),
        });
        if (code === 'wiki_thread_not_found') writeSession();
        return null;
    }
}

// ── Store ───────────────────────────────────────────────────────────────────

export const askStore = {
    getState: () => state,

    subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
    },

    /** Test helper / sign-out: back to empty; in-flight responses are dropped. */
    reset() {
        state = initialState();
        emit();
    },

    /** Bind the conversation to the signed-in user; another user starts empty. */
    bindOwner(ownerKey) {
        if (state.ownerKey === ownerKey) return;
        const previous = state.ownerKey;
        state = initialState(ownerKey);
        emit();
        // A different person in this tab: their predecessor's thread id must
        // not be restored either.
        if (previous !== null) writeSession();
    },

    setInput(input) {
        setState({ input: String(input ?? '') });
    },

    /** Scope chip click. */
    setScope(scope) {
        setState({ scope: { categoryId: scope?.categoryId || null, pageId: scope?.pageId || null } });
    },

    /**
     * A scope handed over by an entry point (Ask {category}, Ask about this
     * page, `?category=` / `?page=`). Selects it and keeps it as a chip.
     */
    presetScope(scope) {
        const next = { categoryId: scope?.categoryId || null, pageId: scope?.pageId || null };
        if (!next.categoryId && !next.pageId) return;
        if (next.pageId) next.categoryId = null;
        setState({ scope: next, extraScope: next });
    },

    /**
     * Drop a scope Ask can't use (plan without category assistants, or the
     * category's assistant switched off): `scope` → "All categories" and/or the
     * preset chip removed. No-op when there is nothing to drop.
     */
    clearScope({ scope = true, extra = true } = {}) {
        const patch = {};
        if (scope && (state.scope.categoryId || state.scope.pageId)) patch.scope = SCOPE_ALL;
        if (extra && state.extraScope) patch.extraScope = null;
        if (Object.keys(patch).length) setState(patch);
    },

    /**
     * Apply a `wikiUi` Ask prefill: scope, then ask now or fill the composer.
     * `allowScope: false` (plan without `wiki_category_assistants`, or the
     * category's assistant is off) drops a category/page scope to the whole
     * Wiki and keeps the question.
     */
    applyPrefill(client, prefill, { allowScope = true } = {}) {
        if (!prefill) return;
        if (allowScope && (prefill.categoryId || prefill.pageId)) {
            askStore.presetScope({ categoryId: prefill.categoryId, pageId: prefill.pageId });
        }
        const question = String(prefill.question || '').trim();
        if (!question) return;
        if (prefill.autoSend) askStore.ask(client, question);
        else setState({ input: question });
    },

    /**
     * `wkAsk(text)`: trim; empty → no-op; one ask at a time. Appends the user
     * bubble and the pending row, clears the composer, posts with the scope
     * and the thread id, then swaps the pending row for the turn.
     */
    ask(client, text) {
        const question = String(text ?? '').trim();
        if (!question || state.sending) return Promise.resolve(null);
        const pendingId = nextRowId();
        setState({
            rows: [
                ...state.rows,
                { id: nextRowId(), role: 'user', text: question },
                { id: pendingId, role: 'ai', pending: true, question },
            ],
            input: '',
            sending: true,
        });
        return runAsk(client, pendingId, question);
    },

    /** Re-run a failed ask in place (the user bubble stays). */
    retry(client, rowId) {
        const row = state.rows.find((r) => r.id === rowId);
        if (!row || !row.error || state.sending) return Promise.resolve(null);
        replaceRow(rowId, { id: rowId, role: 'ai', pending: true, question: row.question });
        setState({ sending: true });
        return runAsk(client, rowId, row.question);
    },

    /** Header "New conversation": thread, votes and traces go; scope and draft stay. */
    newConversation() {
        epochSeq += 1;
        setState({
            epoch: epochSeq,
            threadId: null,
            rows: [],
            sending: false,
            hydrating: false,
            trace: {},
            busy: {},
            fbOpen: null,
            fbReason: null,
            fbText: '',
        });
        writeSession();
    },

    /**
     * After a reload, restore this tab's conversation from the server
     * (`GET /wiki/ask/threads/{id}`). No-op when a thread is already showing.
     */
    async hydrate(client) {
        if (state.rows.length || state.threadId || state.hydrating || !state.ownerKey) return;
        const saved = readSession();
        if (!saved || saved.owner !== state.ownerKey || !saved.threadId) return;
        const { epoch } = state;
        setState({ hydrating: true });
        try {
            const data = await client.get(`/wiki/ask/threads/${enc(saved.threadId)}`);
            if (state.epoch !== epoch) return;
            if (state.rows.length) { setState({ hydrating: false }); return; }
            const rows = [];
            (Array.isArray(data?.turns) ? data.turns : []).forEach((turn) => {
                rows.push({ id: nextRowId(), role: 'user', text: turn?.question || '' });
                rows.push({ id: nextRowId(), role: 'ai', turn: normalizeTurn(turn) });
            });
            setState({ rows, threadId: data?.thread_id || saved.threadId, hydrating: false });
        } catch {
            if (state.epoch !== epoch) return;
            setState({ hydrating: false });
            try { window.sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
        }
    },

    // ── Per-answer UI ───────────────────────────────────────────────────────

    toggleTrace(turnId) {
        setState({ trace: { ...state.trace, [turnId]: !state.trace[turnId] } });
    },

    toggleExact(turnId, n) {
        const key = `${turnId}-${n}`;
        setState({ exact: { ...state.exact, [key]: !state.exact[key] } });
    },

    /** 👎 opens the panel (not a vote yet). No-op once voted. */
    openFeedback(turnId) {
        const turn = findTurn(turnId);
        if (!turn || turn.feedback || turn.can_feedback === false) return;
        setState({ fbOpen: turnId, fbReason: null, fbText: '' });
    },

    cancelFeedback() {
        setState({ fbOpen: null });
    },

    setFeedbackReason(reason) {
        setState({ fbReason: reason || null });
    },

    setFeedbackText(text) {
        setState({ fbText: String(text ?? '') });
    },

    /**
     * Record a vote (`POST /wiki/ask/turns/{id}/feedback`). 👍 sends at once;
     * 👎 is sent from the panel with its reason + note. Votes are final; the
     * toast is the server's (verbatim fallbacks in askCopy).
     */
    async vote(client, turnId, rating) {
        const turn = findTurn(turnId);
        if (!turn || turn.feedback || turn.can_feedback === false || state.busy[turnId]) return null;
        const down = rating === 'down';
        const body = {
            rating: down ? 'down' : 'up',
            reason: down ? (state.fbReason || null) : null,
            note: down ? (state.fbText.trim() || null) : null,
        };
        const { epoch } = state;
        const before = { feedback: turn.feedback ?? null, can_feedback: turn.can_feedback };
        patchTurn(turnId, { feedback: { ...body, created_at: null }, can_feedback: false });
        setBusy(turnId, 'feedback');
        if (down) setState({ fbOpen: null, fbReason: null, fbText: '' });
        try {
            const res = await client.post(`/wiki/ask/turns/${enc(turnId)}/feedback`, body);
            if (state.epoch !== epoch) return null;
            patchTurn(turnId, {
                feedback: res?.feedback || { ...body, created_at: null },
                feedback_label: res?.label || null,
                can_feedback: false,
            });
            setBusy(turnId, null);
            wikiUi.toast(res?.toast || (down ? downToast(res) : ASK_TOASTS.up));
            return res;
        } catch (error) {
            if (state.epoch !== epoch) return null;
            setBusy(turnId, null);
            const code = wikiErrorCode(error);
            const detail = error?.response?.data?.detail;
            if (code === 'wiki_feedback_exists') {
                patchTurn(turnId, { feedback: detail?.feedback || { ...body, created_at: null }, can_feedback: false });
                return null;
            }
            if (code === 'wiki_feedback_not_applicable') {
                patchTurn(turnId, { feedback: null, can_feedback: false });
                return null;
            }
            patchTurn(turnId, before);
            wikiUi.toast(askErrorText(error));
            return null;
        }
    },

    /** "Ask {owner}" → `POST /wiki/ask/turns/{id}/handoff`, then the server's toast. */
    async handoff(client, turnId, ownerName) {
        const turn = findTurn(turnId);
        if (!turn || state.busy[turnId]) return null;
        const { epoch } = state;
        setBusy(turnId, 'handoff');
        try {
            const res = await client.post(`/wiki/ask/turns/${enc(turnId)}/handoff`, { note: null });
            if (state.epoch !== epoch) return null;
            setBusy(turnId, null);
            patchTurn(turnId, {
                handoff: { handoff_id: res?.handoff_id || null, owner: res?.owner || null, created_at: res?.created_at || null },
            });
            wikiUi.toast(res?.toast || ASK_TOASTS.handoff(res?.owner?.name || ownerName));
            return res;
        } catch (error) {
            if (state.epoch !== epoch) return null;
            setBusy(turnId, null);
            if (wikiErrorCode(error) === 'wiki_handoff_no_owner') {
                // Nobody to send it to: the button goes away.
                patchTurn(turnId, { owner: null, gap: turn.gap ? { ...turn.gap, owner: null } : turn.gap });
            }
            wikiUi.toast(askErrorText(error));
            return null;
        }
    },
};

const identity = (value) => value;

/** Subscribe to a slice (a state field or primitive — never a new object). */
export function useAskStore(selector = identity) {
    const read = () => selector(state);
    return useSyncExternalStore(askStore.subscribe, read, read);
}

export default askStore;
